import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router';

import { attemptBootstrap } from '../api/authBootstrap';
import { AppLayout } from '../components/AppLayout';
import { useAuthRefreshTimer } from '../hooks/useAuthRefreshTimer';
import { useAuthStore } from '../stores/authStore';

// Root layout. Holds the cross-cutting auth guard plus the chrome
// switch: public paths render bare (login form, public share viewer);
// authenticated paths get wrapped in <AppLayout> so the top bar
// follows the user across every authed route.
//
// `beforeLoad` is the auth gate (PRD §9.4 + frontend_architecture.md §6,
// B1e PR4): public routes pass through; everything else gets a one-shot
// bootstrap (swap the httpOnly refresh cookie for an access token), and
// is bounced to /login when the bootstrap leaves the store empty. The
// `?redirect=` query carries the original target so the user lands
// where they wanted after auth completes.
export interface RouterContext {
  queryClient: QueryClient;
}

function isPublicPath(pathname: string): boolean {
  if (pathname === '/login') return true;
  // /share/:token — public review surfaces (PRD §9.5). The route itself
  // doesn't exist yet, but reserving the prefix here means the share
  // route can drop in without a guard tweak.
  if (pathname.startsWith('/share/')) return true;
  return false;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ location }) => {
    if (isPublicPath(location.pathname)) return;
    if (useAuthStore.getState().accessToken === null) {
      await attemptBootstrap();
    }
    if (useAuthStore.getState().accessToken === null) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: RootLayout,
});

function RootLayout(): React.JSX.Element {
  useAuthRefreshTimer();
  const { pathname } = useLocation();
  // Public paths render the bare canvas — the login form and public
  // share surfaces own their own framing and don't need the authed
  // shell. Everything else is wrapped in AppLayout so the top bar
  // (theme, user, sign-out) follows the user across the app.
  if (isPublicPath(pathname)) {
    return (
      <main className="bg-bg text-text font-sans min-h-screen">
        <Outlet />
      </main>
    );
  }
  return (
    <main className="font-sans">
      <AppLayout>
        <Outlet />
      </AppLayout>
    </main>
  );
}
